<script lang="ts" setup>
import { ConfigInputType, ConfigSchemaItem } from '@/helper/Types'
import FormElement from '@/components/FormElement.vue'
import { camelize, ValidatorType } from '@/helper/ValidateDirective'
import { ComponentInternalInstance, computed, getCurrentInstance, onMounted, reactive, Ref, ref } from 'vue'
import { containsRuleRequired } from '@/helper/ValidationRules'
import ComboBox from '@/components/ComboBox.vue'

const props = defineProps<{
  schemaItem: ConfigSchemaItem
  config: Record<string, any>
  parentKeys?: (string | number)[] // To be able to identify a nested property inside the config
  options?: string[]
  validator: ValidatorType
}>()

const readonly = reactive({ value: false })
const textInput = ref<HTMLInputElement | undefined>(undefined)

/**
 * Set the readonly set for the key inputs
 */
onMounted((): void => {
  const { prop, itemkey } = getPropRef()
  if (props.schemaItem.type === ConfigInputType.TEXT && props.schemaItem.isUnique && prop[itemkey]) {
    readonly.value = true
  }
})

const selectOptions = computed(() =>
  props.options?.map(option => {
    return {
      label: option,
      value: option
    }
  })
)

const inputType = computed(() => ConfigInputType)
const configInputType = computed((): typeof ConfigInputType => ConfigInputType)

const itemLabel = computed<string>(() => {
  if (!props.schemaItem.label) {
    return ''
  }
  return props.schemaItem.label + (props.schemaItem.expandable ? 's' : '')
})

/**
 * Return a reference on the property in the config and the item key
 * In case of a absolute link (starts with the '@' and separated by '.')
 * the parentKeys get derived from the item key
 * In case of a regular key the parentKey with an array of keys is used
 * */
function getPropRef() {
  let prop = props.config
  let parentKeys = props.parentKeys
  let itemkey = props.schemaItem.key

  if (itemkey.startsWith('@')) {
    itemkey = itemkey.substring(1)
    parentKeys = itemkey.split('.')
    itemkey = (parentKeys.pop() || '').toString()
  }

  let config = props.config
  if (parentKeys) {
    parentKeys.forEach(key => {
      if (config[key] === undefined) {
        config[key] = {}
      }
      config = config[key]

      if (config) {
        prop = config
      }
    })
  }

  return { prop: prop, itemkey: itemkey }
}

function getId(index: number | undefined): string {
  return [
    ...(props.parentKeys || []),
    props.schemaItem.key,
    ...(props.schemaItem.expandable && index !== undefined ? [index] : [])
  ].join('-')
}

function getError(index: number): string | Ref<string> {
  return props.validator[camelize(getId(index))]?.error || ''
}

/**
 * Creates the new array of parent key to be passed to the child ConfigElement
 * In case of a absolute link the parent keys get derived from the item key
 * Absolute keys start with an '@' and the single keys are separated by '.'
 */
function getParentKeys(repeatableIndex: number) {
  const key = []
  if ('@' === props.schemaItem.key[0]) {
    key.push(...props.schemaItem.key.substring(1).split('.'))
  } else {
    if (props.parentKeys) {
      key.push(...props.parentKeys)
    }
    key.push(props.schemaItem.key)
  }
  if (props.schemaItem.expandable) {
    key.push(repeatableIndex)
  }

  return key
}

/**
 * Sets the value of the item in the config object by getting the reference
 * Since CustomSelect emits the selected value on change the e:Event parameter can also be a string
 */
function setConfigValue(e: Event | string, index: number) {
  let val!: string | boolean

  if (typeof e === 'string') {
    val = e
  } else {
    const el = e.currentTarget as HTMLInputElement
    val = el.type === 'checkbox' ? el.checked : el.value
  }

  const { prop, itemkey } = getPropRef()
  if (props.schemaItem.expandable) {
    prop[itemkey][index] = val
  } else {
    prop[itemkey] = val
  }
}

const configValueArray = computed((): unknown[] => {
  const { prop, itemkey } = getPropRef()
  if (!prop) {
    return []
  }
  if (
    prop[itemkey] === undefined ||
    prop[itemkey] === null ||
    (Array.isArray(prop[itemkey]) && !prop[itemkey].length)
  ) {
    prop[itemkey] = createEmptyItem(props.schemaItem)
  }

  return props.schemaItem.expandable ? prop[itemkey] : [prop[itemkey]]
})

function addBtnClicked(): void {
  const emptyItem = createEmptyItem(props.schemaItem, true)
  const { prop, itemkey } = getPropRef()

  if (prop[itemkey] === undefined) {
    prop[itemkey] = []
  }
  prop[itemkey].push((emptyItem as unknown[])[0])
}

const emit = defineEmits<{
  (e: 'delete', prop: any, itemkey: any, index: any, context?: any): void
  (e: 'editkey', context: ComponentInternalInstance | null): void
  (e: 'disable', prop: any, itemkey: any): void
}>()

function deleteBtnClicked(e: Event, index: number): void {
  const { prop, itemkey } = getPropRef()
  emit('delete', prop, itemkey, index)
}

const vm = getCurrentInstance()

function editBtnClicked() {
  emit('editkey', vm)
}

function checkboxClicked(e: Event): void {
  if ((e.currentTarget as HTMLInputElement)?.checked) {
    return
  } // reversed logic since checked state gets reverted
    // after the e.preventDefault
  e.preventDefault()
  const { prop, itemkey } = getPropRef()
  emit('disable', prop, itemkey)
}

function moveBtnClicked(e: Event, up: boolean, index: number): void {
  if ((up && index === 0) || (!up && index === configValueArray.value.length - 1)) {
    return
  }
  configValueArray.value.splice(index + 1 * (up ? -1 : 1), 0, configValueArray.value.splice(index, 1)[0])
}

/**
 * Returns the options for select inputs
 */
function getOptions(item: ConfigSchemaItem, index: number): string[] | undefined {
  if (!('options' in item)) {
    return undefined
  }
  let res = item.options || []
  if (!item.isUnique) {
    return res
  }
  const cva = configValueArray.value as unknown[]
  const ownValue = (cva[index] as any) ? (cva[index] as any)[item.key] : undefined
  const usedValues = cva.map((e: any) => e[item.key]) as string[]
  res = res.filter(e => e === ownValue || !usedValues.includes(e))
  return res
}

const canAdd = computed((): boolean => {
  if (!props.schemaItem) {
    return true
  }
  const el =
    !props.schemaItem.items && props.schemaItem.isUnique
      ? props.schemaItem
      : props.schemaItem.items?.find((el: ConfigSchemaItem) => !!el.isUnique)
  if (!el) {
    return true
  }
  if (el.type === ConfigInputType.TEXT) {
    return true
  }
  return (configValueArray.value || []).length < (el.options?.length || 0)
})

const canDelete = computed(
  (): boolean => !containsRuleRequired(props.schemaItem.rules) || configValueArray.value.length > 1
)

function createEmptyItem(item: ConfigSchemaItem, forceCreate = false): unknown {
  const res: Record<string, any> = {}

  if (!item.items && !item.expandable) {
    return undefined
  }
  if (!item.items && item.expandable) {
    if (containsRuleRequired(item.rules)) {
      return [undefined]
    } else {
      return []
    }
  }
  item.items?.forEach(it => {
    res[it.key] = createEmptyItem(it)
  })

  if (item.expandable) {
    if (containsRuleRequired(item.rules) || forceCreate) {
      return [res]
    }
    return []
  }
  return res
}

defineExpose({
  readonly,
  validator: props.validator,
  createEmptyItem
})
</script>

<template>
  <div
    :id="getId() + '-component'"
    :class="{
      'col-lg-4 col-md-6': !schemaItem.items,
      'col-12': schemaItem.items,
      'config__object-array': schemaItem.items && schemaItem.expandable
    }"
  >
    <article
      v-for="(configValue, configValueIndex) in configValueArray"
      :id="getId(configValueIndex) + '-article'"
      :key="schemaItem.key + configValueIndex"
      class="config__array-item"
    >
      <div
        v-if="schemaItem.items && schemaItem.items.length"
        :class="{ 'is--expandable': schemaItem.expandable }"
        class="config__object"
      >
        <h3
          v-if="schemaItem.items && configValueIndex === 0 && itemLabel && !schemaItem.hideLabel"
          class="config__title"
        >
          {{ itemLabel }}
        </h3>
        <div class="form__row row">
          <div v-if="schemaItem.expandable" class="col-12">
            <div class="config__array-item-controls is--object">
              <button
                :disabled="!canDelete"
                class="icon-btn config__delete-btn"
                @click="deleteBtnClicked($event, configValueIndex)"
              >
                <span>Delete {{ schemaItem.label }}</span><i class="ic-trash" />
              </button>
              <button
                :disabled="configValueIndex >= configValueArray.length - 1"
                class="icon-btn config__btn-move-down"
                @click="moveBtnClicked($event, false, configValueIndex)"
              >
                <i class="ic-arrow-up" />
              </button>
              <button
                :disabled="configValueIndex <= 0"
                class="icon-btn config__btn-move-up"
                @click="moveBtnClicked($event, true, configValueIndex)"
              >
                <i class="ic-arrow-up" />
              </button>
            </div>
          </div>
          <ConfigElement
            v-for="(item, index) in schemaItem.items"
            :key="item.key + index"
            :config="config"
            :options="getOptions(item, configValueIndex)"
            :parent-keys="getParentKeys(configValueIndex)"
            :schema-item="item"
            :validator="validator"
            @delete="(...args) => $emit('delete', ...args)"
            @disable="(...args) => $emit('disable', ...args)"
            @editkey="(...args) => $emit('editkey', ...args)"
          />
        </div>
      </div>
      <div
        v-else
        :class="{
          'is--key': schemaItem.type === inputType.TEXT && schemaItem.isUnique,
          'is--readonly': readonly.value
        }"
        class="config__input-row"
      >
        <FormElement v-if="schemaItem.type === inputType.TOGGLE" class="has--no-margin">
          <label :title="schemaItem.description" class="form__checkbox">
            <input
              :id="getId(configValueIndex)"
              ref="textInput"
              :checked="configValue"
              type="checkbox"
              @change="setConfigValue($event, configValueIndex)"
              @click="checkboxClicked"
            >
            <span class="form__cm" />
            {{ schemaItem.label }}
          </label>
        </FormElement>
        <FormElement
          v-if="schemaItem.type === inputType.TEXT"
          v-validate="schemaItem.rules"
          :error="getError(configValueIndex)"
          :label="configValueIndex === 0 ? itemLabel : ''"
        >
          <input
            :id="getId(configValueIndex)"
            :disabled="readonly.value"
            :placeholder="'Type a value'"
            :value="configValue"
            type="text"
            @blur="readonly.value = schemaItem.isUnique"
            @input="setConfigValue($event, configValueIndex)"
          >
        </FormElement>
        <FormElement
          v-if="schemaItem.type === inputType.SELECT"
          v-validate="schemaItem.rules"
          :error="getError(configValueIndex)"
          :label="configValueIndex === 0 ? schemaItem.label : ''"
          @delete="deleteBtnClicked($event, configValueIndex)"
        >
          <ComboBox
            :id="getId(configValueIndex)"
            :model-value="configValue"
            :options="selectOptions"
            placeholder="Please select"
            @change="setConfigValue($event, configValueIndex)"
          />
        </FormElement>

        <div v-if="schemaItem.type === inputType.TEXT && schemaItem.isUnique" class="config__array-item-controls">
          <button class="icon-btn" @click="editBtnClicked">
            <i class="ic-edit" />
          </button>
        </div>
        <div
          v-if="schemaItem.expandable"
          :class="{ 'is--select': schemaItem.type === configInputType.SELECT }"
          class="config__array-item-controls"
        >
          <button
            :disabled="!canDelete"
            class="icon-btn config__delete-btn"
            @click="deleteBtnClicked($event, configValueIndex)"
          >
            <i class="ic-trash" />
          </button>
          <button
            :disabled="configValueIndex >= configValueArray.length - 1"
            class="icon-btn config__btn-move-down"
            @click="moveBtnClicked($event, false, configValueIndex)"
          >
            <i class="ic-arrow-up" />
          </button>
          <button
            :disabled="configValueIndex <= 0"
            class="icon-btn config__btn-move-up"
            @click="moveBtnClicked($event, true, configValueIndex)"
          >
            <i class="ic-arrow-up" />
          </button>
        </div>
      </div>
      <small v-if="schemaItem.description" class="description-text">{{ schemaItem.description }}</small>
    </article>
    <div v-if="!configValueArray?.length" class="form__element has--no-margin">
      <h3
        v-if="schemaItem.items && schemaItem.items.length && itemLabel && !schemaItem.hideLabel"
        class="config__title"
      >
        {{ itemLabel }}
      </h3>
      <label v-else-if="itemLabel && !schemaItem.hideLabel">{{ itemLabel }}</label>
    </div>
    <div v-if="schemaItem.expandable" v-validate="schemaItem.rules">
      <input :id="camelize(getId())" :value="(configValueArray || []).length || ''" type="hidden">
      <div class="form__error">
        {{ getError(schemaItem.key) }}
      </div>
    </div>
    <div
      v-if="schemaItem.expandable"
      :id="getId() + '-add-btn'"
      :class="{ 'is--add-object': schemaItem.items }"
      class="config__add"
    >
      <button :disabled="!canAdd" class="btn is--secondary" @click="addBtnClicked">
        Add {{ schemaItem.label }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.config {
  &__array-item {
    transition: max-height $animTimeBasic ease;
  }

  &__array-item-controls {
    display: flex;
    margin-right: 0;
    margin-left: auto;
    align-items: center;
    justify-content: flex-end;

    i[class^='ic-'],
    i[class*=' ic-'] {
      font-size: rem-calc(24);
    }

    .config__delete-btn {
      @include hoverChildFadeIn(0.3s, 'span');

      i[class^='ic-'],
      i[class*=' ic-'] {
        font-size: rem-calc(28);
      }
    }

    &.is--object {
      i[class^='ic-'],
      i[class*=' ic-'] {
        font-size: rem-calc(32);
      }
    }
  }

  &__btn-move-down {
    i[class^='ic-'],
    i[class*=' ic-'] {
      transform: rotate(180deg);
    }
  }

  &__input-row {
    display: flex;
    align-items: center;
    position: relative;

    &.is--key {
      &.is--readonly {
        .form__element {
          input {
            background: $bgColor;
          }
        }
      }

      &:not(.is--readonly) {
        .config__array-item-controls {
          display: none;
        }
      }
    }

    @include hoverChildFadeIn(0.3s, '.config__array-item-controls');

    .config__array-item-controls {
      position: absolute;
      right: rem-calc(4);
      bottom: rem-calc(21); //16 margin form__element + 5 to center inside input

      .icon-btn {
        height: rem-calc(28);
      }

      &.is--select {
        right: rem-calc(32);
        background: rgba(255, 255, 255, 0.9);
      }
    }

    .form__element {
      flex-grow: 1;
      max-width: 100%;

      &.is--invalid {
        & + .config__array-item-controls {
          bottom: rem-calc(50);
        }
      }
    }
  }

  &__title {
    font-weight: bold;
  }

  &__delete {
    display: flex;
    margin-top: 0;
    margin-bottom: 0;

    .icon-btn {
      display: flex;
      align-items: center;

      [class^='ic-'],
      [class*=' ic-'] {
        font-size: rem-calc(32);
      }

      span {
        display: none;
      }

      &:hover {
        span {
          display: block;
        }
      }
    }

    button {
      margin-left: auto;
      margin-right: 0;
    }
  }

  &__object-array {
    .config__array-item {
      &:last-of-type {
        & > .config__object {
          border-bottom: none;
          padding-bottom: 0;
        }
      }

      &:first-of-type {
        & > .config__object {
          border-top: none;
          padding-top: 0;
        }
      }
    }
  }

  &__object {
    border-bottom: 1px solid $borderColorLight;
    margin-top: -1px;
    padding-bottom: rem-calc($grid-gutter-width);
    padding-top: rem-calc($grid-gutter-width);
    position: relative;

    .config__object {
      border-top: 0;
      border-bottom: 0;

      .config__title {
        @include themify($themes) {
          font-size: rem-calc(themed('h4Size'));
          font-weight: bold;
          line-height: rem-calc(themed('h4LineHeight'));
        }
      }
    }

    .config__object-array {
      .config__title {
        @include themify($themes) {
          font-size: rem-calc(themed('h4Size'));
          font-weight: bold;
          line-height: rem-calc(themed('h4LineHeight'));
        }
      }
    }
  }

  &__object-array {
    position: relative;
    padding-top: rem-calc($grid-gutter-width * 2);
    padding-bottom: rem-calc($grid-gutter-width * 2);

    &:last-child {
      &:after {
        display: none;
      }
    }

    & + .config__object-array {
      margin-top: rem-calc(-$grid-gutter-width * 0.5 - 1);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      height: 1px;
      background: $borderColorLight;
      left: rem-calc(-$sectionPadding + $grid-gutter-width * 0.5);
      right: rem-calc(-$sectionPadding + $grid-gutter-width * 0.5);
    }

    &:before {
      top: 0;
    }

    &:after {
      bottom: 0;
    }
  }

  &__add {
    &.is--add-object {
      padding-top: rem-calc($grid-gutter-width * 1.5);
      border-top: 1px solid $borderColorLight;
    }
  }
}

.form {
  &__section {
    .form__row {
      &:last-child {
        margin-bottom: 0;
      }

      .form__element.has--no-margin {
        margin-bottom: 0;
      }
    }
  }
}

.description-text {
  font-size: 12px;
  color: $placeholderColor;
  margin-bottom: 1rem;
  display: block;
  line-height: 1.3;
}
</style>
